import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import Label from '../ui/Label';
import Input from '../ui/Input';
import Textarea from '../ui/Textarea';
import Button from '../ui/Button';
import { FunctionalSelect, SelectItem } from '../ui/Select';
import CriteriaBuilder from './CriteriaBuilder';
import { defaultCriteriaRoot, deepClone, criteriaHasTableReferences, isConditionValueError } from './criteriaUtils';
import { api } from '../../services/authService';
import { FASTCUBE_ORG } from '../../services/organizationsService';
import './CriteriaBuilder.css';

export const SCHEDULE_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

export const SCHEDULE_DAYS_OF_WEEK = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' },
];

export const SCHEDULE_DAYS_OF_MONTH = [
  { value: 'beginning', label: 'Beginning' },
  { value: 'end', label: 'End' },
  ...Array.from({ length: 28 }, (_, i) => ({
    value: String(i + 1),
    label: String(i + 1),
  })),
];

function formatApiErrorDetail(detail) {
  if (detail == null) return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && e.msg != null ? e.msg : JSON.stringify(e))).join('\n');
  }
  if (typeof detail === 'object') return JSON.stringify(detail);
  return String(detail);
}

function segmentRunStatusBadgeClass(processingStatus) {
  const s = (processingStatus || '').toLowerCase();
  if (s === 'complete' || s === 'completed') return 'bg-blue-100 text-blue-800';
  if (s === 'failed') return 'bg-red-100 text-red-800';
  if (s === 'pending') return 'bg-yellow-100 text-yellow-800';
  if (s === 'started' || s === 'processing') return 'bg-green-100 text-green-800';
  return 'bg-gray-100 text-gray-800';
}

function canDownloadSegmentRun(executionId, runStatus) {
  const s = (runStatus || '').toLowerCase();
  return Boolean(executionId) && (s === 'complete' || s === 'completed');
}

const SegmentSqlEditor = ({
  criteria,
  onCriteriaChange,
  sql,
  onSqlChange,
  md5Count,
  onMd5CountChange,
  schedule = '',
  onScheduleChange,
  scheduleDayOfWeek = '',
  onScheduleDayOfWeekChange,
  scheduleDayOfMonth = '',
  onScheduleDayOfMonthChange,
  executionId = '',
  runStatus = '',
  onDownload,
  onRunSegment,
  runningSegment = false,
  onSave,
  saving,
  organizationId = '',
  organizationName = '',
  segmentId = null,
  onRunCountSuccess,
}) => {
  const isFastCubeOrg = organizationName === FASTCUBE_ORG;
  const [runCountLoading, setRunCountLoading] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [downloadStale, setDownloadStale] = useState(false);
  const hasGeneratedSql = Boolean((sql || '').trim());
  const hasCompletedDownload = canDownloadSegmentRun(executionId, runStatus);
  const showDownload = !runningSegment && hasCompletedDownload;

  const handleCriteriaChange = (next) => {
    onCriteriaChange(next);
    setValidationError('');
    onMd5CountChange(null);
    if (executionId) {
      setDownloadStale(true);
    }
  };

  // New Create File run started — download will match criteria once it completes.
  useEffect(() => {
    const s = (runStatus || '').toLowerCase();
    if (s === 'pending' || s === 'started' || s === 'processing') {
      setDownloadStale(false);
    }
  }, [runStatus, executionId]);

  const handleScheduleChange = (value) => {
    onScheduleChange(value || '');
    if (value !== 'weekly') onScheduleDayOfWeekChange('');
    if (value !== 'monthly') onScheduleDayOfMonthChange('');
  };

  useEffect(() => {
    if (!criteriaHasTableReferences(criteria) || !organizationId) {
      setValidationError('');
      onSqlChange('');
      onMd5CountChange(null);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const res = await api.post('/api/v2/segments/preview-sql', {
          criteria,
          organization_id: parseInt(organizationId, 10),
        });
        if (!cancelled) {
          setValidationError('');
          onSqlChange(res.data?.sql || '');
        }
      } catch (e) {
        if (!cancelled) {
          setValidationError(formatApiErrorDetail(e.response?.data?.detail) || 'Failed to generate SQL');
        }
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [criteria, organizationId, onSqlChange, onMd5CountChange]);

  const runCount = async () => {
    if (!organizationId) {
      setValidationError('Select an organization before running count.');
      return;
    }
    setValidationError('');
    setRunCountLoading(true);
    try {
      const payload = {
        criteria,
        organization_id: parseInt(organizationId, 10),
      };
      if (segmentId != null) {
        payload.segment_id = segmentId;
      }
      const res = await api.post('/api/v2/segments/run-count', payload);
      if (res.data?.sql) onSqlChange(res.data.sql);
      const n = Number(res.data?.count);
      const count = Number.isFinite(n) ? n : null;
      onMd5CountChange(count);
      if (segmentId != null && onRunCountSuccess) {
        onRunCountSuccess({
          segmentId,
          count,
        });
      }
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || 'Failed to run count';
      setValidationError(msg);
      onMd5CountChange(null);
    } finally {
      setRunCountLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <Label>Criteria</Label>
        <CriteriaBuilder criteria={criteria} onChange={handleCriteriaChange} />
        {validationError && !isConditionValueError(validationError) && (
          <p className="mt-2 text-sm text-red-600 whitespace-pre-line" role="alert">
            {validationError}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label>Schedule</Label>
        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-[10rem]">
            <FunctionalSelect
              value={schedule || ''}
              onValueChange={handleScheduleChange}
              placeholder="None"
            >
              {SCHEDULE_OPTIONS.map((option) => (
                <SelectItem key={option.value || 'none'} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </FunctionalSelect>
          </div>
          {schedule === 'weekly' && (
            <div className="min-w-[10rem]">
              <FunctionalSelect
                value={scheduleDayOfWeek || ''}
                onValueChange={(value) => onScheduleDayOfWeekChange(value || '')}
                placeholder="Select day"
              >
                {SCHEDULE_DAYS_OF_WEEK.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </FunctionalSelect>
            </div>
          )}
          {schedule === 'monthly' && (
            <div className="min-w-[10rem]">
              <FunctionalSelect
                value={scheduleDayOfMonth || ''}
                onValueChange={(value) => onScheduleDayOfMonthChange(value || '')}
                placeholder="Select day"
              >
                {SCHEDULE_DAYS_OF_MONTH.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </FunctionalSelect>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" onClick={runCount} disabled={runCountLoading || saving || runningSegment}>
          {runCountLoading ? (
            <>
              <Loader2 className="h-4 w-4 mr-1 animate-spin" aria-hidden />
              Running…
            </>
          ) : (
            'Run count'
          )}
        </Button>
        {onRunSegment != null && (
          <Button
            type="button"
            variant="outline"
            onClick={onRunSegment}
            disabled={!hasGeneratedSql || runningSegment || saving || runCountLoading}
          >
            {runningSegment ? (
              <>
                <Loader2 className="h-4 w-4 mr-1 animate-spin" aria-hidden />
                Running…
              </>
            ) : (
              'Create File'
            )}
          </Button>
        )}
        {onSave && (
          <Button
            type="button"
            variant="outline"
            onClick={onSave}
            disabled={!hasGeneratedSql || saving || runCountLoading || runningSegment}
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-1 animate-spin" aria-hidden />
                Saving…
              </>
            ) : (
              'Save'
            )}
          </Button>
        )}
        {showDownload && onDownload && (
          <Button
            type="button"
            variant="outline"
            onClick={onDownload}
            disabled={downloadStale}
            title={
              downloadStale
                ? 'Criteria changed — run Create File again to download an updated file'
                : undefined
            }
          >
            Download
          </Button>
        )}
      </div>

      <div className="space-y-2 max-w-xs">
        <Label htmlFor="segment-count">Count</Label>
        <Input
          id="segment-count"
          readOnly
          value={
            runCountLoading
              ? ''
              : md5Count != null
                ? Number(md5Count).toLocaleString()
                : ''
          }
          placeholder={runCountLoading ? 'Running…' : 'Run count to populate'}
          className="bg-gray-50"
          aria-busy={runCountLoading}
        />
      </div>

      <div className="flex flex-wrap items-end gap-4">
        {isFastCubeOrg && (
          <div className="space-y-2 flex-1 min-w-[12rem] max-w-md">
            <Label htmlFor="segment-execution-id">Execution ID</Label>
            <Input
              id="segment-execution-id"
              readOnly
              value={runningSegment ? '' : executionId || ''}
              placeholder={runningSegment ? 'Running…' : 'Create File to populate'}
              className="bg-gray-50"
              aria-busy={runningSegment}
            />
          </div>
        )}
        <div className="space-y-2 shrink-0">
          <Label htmlFor="segment-run-status">Run Status</Label>
          <div
            id="segment-run-status"
            className="flex h-10 min-w-[7rem] items-center rounded-md border border-input bg-gray-50 px-3"
          >
            {runningSegment ? (
              <span className="text-sm text-gray-400">Running…</span>
            ) : runStatus ? (
              <span
                className={`px-2 py-0.5 rounded text-xs font-medium ${segmentRunStatusBadgeClass(runStatus)}`}
              >
                {runStatus}
              </span>
            ) : (
              <span className="text-sm text-gray-400">—</span>
            )}
          </div>
        </div>
      </div>

      {isFastCubeOrg && (
        <div className="space-y-2">
          <Label htmlFor="segment-generated-sql">Generated Databricks SQL</Label>
          <Textarea
            id="segment-generated-sql"
            value={sql}
            readOnly
            rows={10}
            placeholder="SQL updates as you build criteria…"
            className="segment-sql-preview bg-gray-50"
          />
        </div>
      )}
    </div>
  );
};

export function initialCriteriaState(existingCriteria) {
  if (existingCriteria && existingCriteria.kind === 'group') {
    return deepClone(existingCriteria);
  }
  return defaultCriteriaRoot();
}

export default SegmentSqlEditor;
